import classNames from 'classnames';
import type { ChordGroup as D3ChordGroup } from 'd3';
import { arc } from 'd3';

import { truncateText } from '../../utilities';
import styles from './chord-chart.module.css';
import { ChordGroupTooltip } from './chord-group-tooltip';
import {
  getRadialTooltipAnchor,
  getRadialTooltipPlacement,
} from './chord-tooltip-position';
import type { ChordFlowMode } from './types';

export type GroupTick = {
  /** d3-chord angle (0 = 12 o'clock, clockwise). */
  angle: number;
  label: string;
};

type ChordGroupProps = {
  group: D3ChordGroup;
  label: string;
  color: string;
  inflow: number;
  outflow: number;
  innerRadius: number;
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
  labelPathId: string;
  labelStartAngle?: number;
  labelEndAngle?: number;
  ticks?: GroupTick[];
  tickFontSize?: number;
  tickLineLength?: number;
  tickLabelOffset?: number;
  flowMode?: ChordFlowMode;
  isFaded: boolean;
  isHovered: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  /** When false, hover handlers, tooltip, and the pointer cursor are skipped. */
  interactive?: boolean;
};

export const ChordGroup = ({
  group,
  label,
  color,
  inflow,
  outflow,
  innerRadius,
  outerRadius,
  labelOffset,
  labelFontSize,
  labelPathId,
  labelStartAngle,
  labelEndAngle,
  ticks,
  tickFontSize = 10,
  tickLineLength = 4,
  tickLabelOffset = 8,
  flowMode = 'directional',
  isFaded,
  isHovered,
  onHoverStart,
  onHoverEnd,
  interactive = true,
}: ChordGroupProps) => {
  const arcGenerator = arc<D3ChordGroup>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius);
  const arcPath = arcGenerator(group) ?? undefined;

  const midAngle = (group.startAngle + group.endAngle) / 2;
  const tooltipAnchor = getRadialTooltipAnchor({
    angle: midAngle,
    outerRadius,
    labelOffset,
    labelFontSize,
  });
  const tooltipPlacement = getRadialTooltipPlacement(midAngle);

  const labelStart = labelStartAngle ?? group.startAngle;
  const labelEnd = labelEndAngle ?? group.endAngle;
  const labelMidAngle = (labelStart + labelEnd) / 2;
  const labelRadius = getLabelRadius({
    midAngle: labelMidAngle,
    outerRadius,
    labelOffset,
    labelFontSize,
  });

  const availableLabelWidth = Math.max(
    0,
    (labelEnd - labelStart) * labelRadius - LABEL_TEXT_PADDING
  );

  const displayLabel = truncateText({
    text: label,
    maxWidth: availableLabelWidth,
    fontSize: labelFontSize,
  });

  const labelPath = getCurvedLabelPath({
    startAngle: labelStart,
    endAngle: labelEnd,
    outerRadius,
    labelOffset,
    labelFontSize,
  });

  return (
    <g
      className={classNames(styles.group, {
        [styles.faded ?? '']: isFaded,
        [styles.nonInteractive ?? '']: !interactive,
      })}
      onMouseEnter={interactive ? onHoverStart : undefined}
      onMouseLeave={interactive ? onHoverEnd : undefined}
      data-testid="chord-chart__group"
    >
      <defs>
        <path id={labelPathId} d={labelPath} />
      </defs>
      <path d={arcPath} fill={color} stroke={color} className={styles.arc} />
      {ticks?.map((tick, idx) => (
        <GroupTickMark
          key={idx}
          tick={tick}
          outerRadius={outerRadius}
          lineLength={tickLineLength}
          labelOffset={tickLabelOffset}
          fontSize={tickFontSize}
        />
      ))}
      {interactive ? (
        <ChordGroupTooltip
          label={label}
          inflow={inflow}
          outflow={outflow}
          isOpen={isHovered}
          placement={tooltipPlacement}
          flowMode={flowMode}
        >
          <circle
            r={0}
            cx={tooltipAnchor.x}
            cy={tooltipAnchor.y}
            pointerEvents="none"
          />
        </ChordGroupTooltip>
      ) : null}
      <text
        fontSize={labelFontSize}
        className={styles.label}
        pointerEvents="none"
      >
        <textPath
          href={`#${labelPathId}`}
          startOffset="50%"
          textAnchor="middle"
        >
          {displayLabel}
        </textPath>
      </text>
    </g>
  );
};

type GroupTickMarkProps = {
  tick: GroupTick;
  outerRadius: number;
  lineLength: number;
  labelOffset: number;
  fontSize: number;
};

const GroupTickMark = ({
  tick,
  outerRadius,
  lineLength,
  labelOffset,
  fontSize,
}: GroupTickMarkProps) => {
  // d3-chord: angle 0 = 12 o'clock, clockwise. (angle - 90°) makes local +x radial.
  const angleDeg = (tick.angle * 180) / Math.PI - 90;
  // Flip text past 6 o'clock so labels stay right-side up.
  const isFlipped = tick.angle > Math.PI;
  const labelX = lineLength + labelOffset;
  return (
    <g
      transform={`rotate(${angleDeg}) translate(${outerRadius}, 0)`}
      className={styles.tick}
    >
      <line x2={lineLength} className={styles.tickLine} />
      <text
        x={labelX}
        dy="0.32em"
        textAnchor={isFlipped ? 'end' : 'start'}
        transform={
          isFlipped ? `rotate(180) translate(${-2 * labelX}, 0)` : undefined
        }
        fontSize={fontSize}
      >
        {tick.label}
      </text>
    </g>
  );
};

const polar = (angle: number, radius: number): { x: number; y: number } => ({
  x: radius * Math.sin(angle),
  y: -radius * Math.cos(angle),
});

const LABEL_FLIP_LOWER = Math.PI / 2;
const LABEL_FLIP_UPPER = (3 * Math.PI) / 2;

const LABEL_TEXT_PADDING = 2;

export const isLabelFlipped = (midAngle: number): boolean =>
  midAngle > LABEL_FLIP_LOWER && midAngle < LABEL_FLIP_UPPER;

export const getLabelRadius = ({
  midAngle,
  outerRadius,
  labelOffset,
  labelFontSize,
}: {
  midAngle: number;
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
}): number =>
  isLabelFlipped(midAngle)
    ? outerRadius + labelOffset + labelFontSize
    : outerRadius + labelOffset;

const getCurvedLabelPath = ({
  startAngle,
  endAngle,
  outerRadius,
  labelOffset,
  labelFontSize,
}: {
  startAngle: number;
  endAngle: number;
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
}): string => {
  const midAngle = (startAngle + endAngle) / 2;
  const isFlipped = isLabelFlipped(midAngle);
  const radius = getLabelRadius({
    midAngle,
    outerRadius,
    labelOffset,
    labelFontSize,
  });
  const a0 = isFlipped ? endAngle : startAngle;
  const a1 = isFlipped ? startAngle : endAngle;
  const p0 = polar(a0, radius);
  const p1 = polar(a1, radius);
  const largeArc = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;
  const sweep = isFlipped ? 0 : 1;
  return `M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${p1.x} ${p1.y}`;
};
